import React, { useEffect, useMemo, useState } from 'react'
import { Button, Col, Row, Spinner, Alert } from 'react-bootstrap'
import { useAnalyticsDownloads } from './hooks/useAnalyticsDownloads'
import { useSelector } from 'react-redux'
import { addUpgradeReason, hasFeature } from 'utils/general'
import styles from './AnalyticsDownloads.module.css'
import { F_DOWNLOAD_ANALYTICS } from '../../../constants/plans/constants'

const PERIODS = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '6mo', label: 'Last 6 months' },
  { value: '12mo', label: 'Last 12 months' },
  { value: 'custom', label: 'Custom range' }
]

const AnalyticsDownloads = ({ domain }) => {
  const productId = useSelector((state) => state.manage.userProfile.productId) || null
  const [selectedPeriod, setSelectedPeriod] = useState('30d')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const hasAccess = useMemo(() => {
    return hasFeature(productId, F_DOWNLOAD_ANALYTICS)
  }, [productId])

  const { downloadStats, isLoading, error, hasDownloads, handleUpgrade } = useAnalyticsDownloads({
    domain,
    period: selectedPeriod,
    startDate: selectedPeriod === 'custom' ? startDate : undefined,
    endDate: selectedPeriod === 'custom' ? endDate : undefined
  })

  // Check if site can have downloads
  const siteData = useSelector((state) => state.modals.analyticsModalPayload?.site)
  const canHaveDownloads = useMemo(() => {
    if (!siteData) return true

    // Sites that cannot have downloads:
    // - Type is 'html' or 'website' (not files)
    // - Type is 'zip' (zip files are usually extracted, not downloaded)
    // - Downloads are disabled in settings
    const isWebsiteOrZip = ['html', 'website', 'zip'].includes(siteData.type)
    const downloadsDisabled = siteData.siteSettings?.disableDownloads === true

    return !isWebsiteOrZip && !downloadsDisabled
  }, [siteData])

  useEffect(() => {
    if (!isLoading && !hasAccess) addUpgradeReason('View Download Analytics')
  }, [isLoading, hasAccess])

  if (isLoading) {
    return (
      <div className="text-center p-3">
        <Spinner animation="border" variant="primary" />
      </div>
    )
  }

  if (!hasAccess) {
    return (
      <div className="text-center my-4 bg-light rounded">
        <div className={`${styles.trialCTA} mb-2`}>
          <div className="bold">Track file downloads and understand user engagement</div>
          <Button className="mt-md-0 mt-2" variant="purple" onClick={handleUpgrade}>
            Upgrade to unlock →
          </Button>
        </div>
        <img
          className={styles['tracked-links-gif']}
          src="/assets/img/gifs/tracked-links.gif"
          alt="Download Analytics Preview"
        />
      </div>
    )
  }

  if (!canHaveDownloads) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyStateIcon}>📊</div>
        <div className={styles.emptyStateTitle}>No downloadable files</div>
        <p className={styles.emptyStateDescription}>
          You don't have any live files that can be downloaded. This feature tracks downloads for PDFs, documents,
          images, and other files hosted on your site. Websites, extracted zip files, and sites with downloads disabled
          are not tracked here.
        </p>
      </div>
    )
  }

  return (
    <div className="p-2">
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <div className={styles.periodSelector}>
        {PERIODS.map((period) => (
          <button
            key={period.value}
            className={`${styles.periodButton} ${selectedPeriod === period.value ? styles.active : ''}`}
            onClick={() => setSelectedPeriod(period.value)}
          >
            {period.label}
          </button>
        ))}
      </div>

      {selectedPeriod === 'custom' && (
        <div className={styles.datePickerContainer}>
          <label htmlFor="startDate">From:</label>
          <input
            id="startDate"
            type="date"
            className={styles.dateInput}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <label htmlFor="endDate">To:</label>
          <input
            id="endDate"
            type="date"
            className={styles.dateInput}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
      )}

      {hasDownloads ? (
        <>
          <Row className="fw-bold d-none d-md-flex pb-2 mt-3">
            <Col md={10}>File Name</Col>
            <Col md={2} className="text-end">
              Downloads
            </Col>
          </Row>
          <div className={styles.downloadsContainer}>
            <div className={styles.downloadsList}>
              {downloadStats.map((stat, index) => (
                <Row key={`${stat.fileName}-${index}`} className="align-items-center border mb-2 mb-md-0 py-2">
                  <Col xs={12} md={10} className="mb-2 mb-md-0 fw-medium text-break">
                    {stat.fileName}
                  </Col>
                  <Col xs={6} md={2} className="text-md-end">
                    <span className="d-inline d-md-none text-muted small me-1">Downloads:</span>
                    {stat.uniqueDownloaders}
                  </Col>
                </Row>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>
            <img src="/assets/icons/download-file.svg" alt="No downloads" width="48" height="48" />
          </div>
          <div className={styles.emptyStateTitle}>No downloads yet</div>
          <p className={styles.emptyStateDescription}>
            When visitors download files from your site, you'll see the statistics here.
          </p>
        </div>
      )}
    </div>
  )
}

export default AnalyticsDownloads
